import { useState, type ReactNode } from "react";
import { useServiceAdmin } from "./use-service-admin.ts";
import { formatDateTime, isoAttr } from "../../../lib/datetime.ts";

/**
 * The agent's Oathera Passport in the AIS console: the signed descriptive document the AIS issues
 * for an enrolled agent in the ServiceNow AI Passport v1 wire format, plus its current revocation
 * answer. Opening the panel reads `GET /admin/api/agents/:ref/passport`, which issues revision 1 if
 * the agent has none yet. Read-only: revocation follows the agent's own lifecycle (suspend,
 * reinstate, revoke, delete, owner deprovisioning).
 */

interface RevocationAnswer {
  readonly revoked: boolean;
  readonly reason?: string;
  readonly state_version: number;
  readonly min_required_revision: number;
}

type PassportView =
  | {
      readonly available: true;
      readonly passport: Record<string, unknown>;
      readonly subject: string;
      readonly revision: number;
      readonly min_required_revision: number;
      readonly valid_from: string;
      readonly valid_to: string;
      readonly kid: string;
      readonly content_hash: string;
      readonly revocation: RevocationAnswer;
    }
  | { readonly available: false; readonly reason: string };

/** `GET /admin/api/agents/:ref/passport`. */
export function passportPath(agentRef: string): string {
  return `agents/${encodeURIComponent(agentRef)}/passport`;
}

const UNAVAILABLE: Readonly<Record<string, string>> = {
  no_accountable_manager:
    "No Passport can be issued: the agent has no OIDC-verified person recorded as approving its enrollment, so the Passport would name no accountable manager.",
  accountability_lapsed:
    "No Passport can be issued: the person who approved this agent has been deprovisioned, so no accountable manager is in force.",
};

const REASON: Readonly<Record<string, string>> = {
  withdrawn_by_operator: "revoked or suspended by an administrator",
  governance_withdrawn: "the agent was deleted",
  accountability_lapsed: "the approving owner was deprovisioned",
  no_active_passport: "the current Passport has expired",
  revision_withdrawn: "earlier revisions are withdrawn; this one is current",
};

function when(iso: string): ReactNode {
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? <time dateTime={isoAttr(ms)}>{formatDateTime(ms)}</time> : iso;
}

function PassportBody(props: { readonly agentRef: string }): ReactNode {
  const query = useServiceAdmin<PassportView>("ais", passportPath(props.agentRef), ["agents", props.agentRef, "passport"]);
  if (query.isPending) return <p className="svc-admin__muted">Loading the Passport…</p>;
  if (query.isError) {
    const message = query.error.status === 404
      ? "Oathera Passports are not enabled on this AIS."
      : "The Passport could not be loaded. Try again.";
    return <p className="svc-admin__muted">{message}</p>;
  }
  const view = query.data;
  if (!view.available) return <p className="svc-admin__muted">{UNAVAILABLE[view.reason] ?? `No Passport can be issued (${view.reason}).`}</p>;
  const { revocation } = view;
  // One document: the signed Passport, which carries the agent's enrollment record in its signed
  // `oathera` member. The block and the download are the same JSON.
  const json = JSON.stringify(view.passport, null, 2);
  const download = `data:application/json;charset=utf-8,${encodeURIComponent(json)}`;
  return (
    <>
      <dl className="svc-admin__kv">
        <div className="svc-admin__kv-row">
          <dt>Subject</dt>
          <dd>{view.subject}</dd>
        </div>
        <div className="svc-admin__kv-row">
          <dt>Revision</dt>
          <dd>{view.revision}{view.min_required_revision > 1 ? ` (earlier than ${view.min_required_revision} withdrawn)` : ""}</dd>
        </div>
        <div className="svc-admin__kv-row">
          <dt>Valid</dt>
          <dd>{when(view.valid_from)} to {when(view.valid_to)}</dd>
        </div>
        <div className="svc-admin__kv-row">
          <dt>Signing key</dt>
          <dd>{view.kid} (ES256)</dd>
        </div>
        <div className="svc-admin__kv-row">
          <dt>Revocation</dt>
          <dd>
            {revocation.revoked
              ? `Revoked${revocation.reason ? `: ${REASON[revocation.reason] ?? revocation.reason}` : ""}`
              : `Not revoked${revocation.reason ? ` (${REASON[revocation.reason] ?? revocation.reason})` : ""}`}
            {` · state version ${revocation.state_version}`}
          </dd>
        </div>
      </dl>
      <p className="svc-admin__muted">
        Accountable manager and risk come from OATHERA&apos;s own records: the verified person who approved the enrollment,
        and a risk classification of “unassessed” until your organisation records an assessment.
      </p>
      <div className="svc-admin__actions">
        <a className="svc-admin__btn" href={download} download={`oathera-passport-r${view.revision}.json`}>
          Download Oathera Passport
        </a>
      </div>
      <details className="svc-admin__raw" open>
        <summary>Oathera Passport JSON</summary>
        <pre>{json}</pre>
      </details>
    </>
  );
}

export function AgentPassportPanel(props: { readonly agentRef: string | undefined }): ReactNode {
  const [open, setOpen] = useState(false);
  return (
    <section className="svc-lifecycle" aria-label="Oathera Passport">
      <div className="svc-adm2__card-head">
        <h4 className="svc-admin__section-title">Oathera Passport</h4>
        {props.agentRef !== undefined && !open && (
          <div className="svc-admin__actions">
            <button type="button" className="svc-admin__btn" onClick={() => setOpen(true)}>
              Show Passport
            </button>
          </div>
        )}
      </div>
      {props.agentRef === undefined && <p className="svc-admin__muted">This agent has no reference the console can address, so no Passport can be shown.</p>}
      {props.agentRef !== undefined && open && <PassportBody agentRef={props.agentRef} />}
    </section>
  );
}
